import { NetworkStatus, gql, useQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { OrganizationProfileImage } from '@/components/OrganizationProfileImage';
import { OrganizationProfileImage_OrganizationFragment } from '@/components/graphql-types/OrganizationProfileImage_OrganizationFragment';
import { ATTRIBUTE_FRAGMENT } from '@/modules/attributes';
import {
  MembershipStatus,
  MembershipTermAlert,
  OrganizationSearchInput,
  OrganizationSearchInput_Filter,
} from '@/graphql-types/globalTypes';

const ORGANIZATIONS_QUERY = gql`
  query useOrganizationsPagination_OrganizationSearch(
    $input: OrganizationSearchInput!
  ) {
    organizationSearch(input: $input) {
      results {
        _id
        displayName
        hidden
        isSponsor
        isMember
        currentUserOrganizationPermission {
          canManageOrganizationProfile
        }
        accountOwners {
          _id
          email
        }
        profileAttributes(input: {}) {
          results {
            attribute {
              _id
              ...AttributeFragment
            }
          }
        }
        membershipData {
          mostRelevantMembershipTermAlert
          membershipStatus
          currentMembershipTerm {
            _id
            membershipType {
              _id
              name
            }
          }
        }
        ...OrganizationProfileImage_OrganizationFragment
      }
      totalCount
    }
  }
  ${OrganizationProfileImage.organizationFragment}
  ${ATTRIBUTE_FRAGMENT}
`;

const LIMIT = 20;

export type OrganizationResult = OrganizationProfileImage_OrganizationFragment & {
  displayName: string;
  hidden: boolean;
  isSponsor: boolean;
  isMember?: boolean;
  accountOwners: { _id: string; email: string | null }[];
  profileAttributes: {
    __typename: 'ProfileAttributesOutput';
    results: Array<{
      __typename: 'ProfileAttribute';
      attribute: {
        _id: string;
        __typename: string;
        label?: string | null;
        slug?: string | null;
        freeTextValue?: string | null;
      };
    }>;
  };
  membershipData: {
    mostRelevantMembershipTermAlert?: MembershipTermAlert | null;
    membershipStatus?: MembershipStatus;
    currentMembershipTerm?: {
      _id: string;
      membershipType?: { _id: string; name: string };
    } | null;
  } | null;
  currentUserOrganizationPermission?: {
    canManageOrganizationProfile?: boolean;
  } | null;
};

export type IUseOrganizationsPagination = {
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  organizations: OrganizationResult[];
  totalCount: number;
  searchText: string;
  setSearchText: (val: string) => void;
};

export const useOrganizationsPagination = (
  searchText: string,
  filter?: OrganizationSearchInput_Filter,
  options?: { enabled?: boolean }
): IUseOrganizationsPagination => {
  const enabled = options?.enabled ?? true;
  const variables = useMemo(
    () => ({
      input: {
        offset: 0,
        limit: LIMIT,
        filter: {
          ...(filter || {}),
          searchText: searchText || undefined,
        },
      },
    }),
    [searchText, filter]
  );

  const { fetchMore, data, networkStatus } = useQuery(ORGANIZATIONS_QUERY, {
    variables,
    skip: !enabled,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const results: OrganizationResult[] = data?.organizationSearch?.results ?? [];
  const totalCount: number = data?.organizationSearch?.totalCount ?? 0;

  const isLoading =
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables;

  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;
  const hasMore = results.length < totalCount;

  const loadMore = useCallback(async () => {
    if (isLoading || isFetchingMore || !hasMore) return;
    await fetchMore({
      variables: {
        ...variables,
        input: {
          ...variables.input,
          offset: results.length,
          limit: LIMIT,
        },
      },
      updateQuery: (prev: any, { fetchMoreResult }: any) => {
        if (!fetchMoreResult) return prev;
        const merged = [
          ...prev.organizationSearch.results,
          ...fetchMoreResult.organizationSearch.results,
        ];
        const unique = merged.filter(
          (item: any, idx: number, self: any[]) =>
            idx === self.findIndex((x: any) => x._id === item._id)
        );
        const didAddResults =
          unique.length > prev.organizationSearch.results.length;

        return {
          ...prev,
          organizationSearch: {
            ...fetchMoreResult.organizationSearch,
            results: unique,
            totalCount: didAddResults
              ? fetchMoreResult.organizationSearch.totalCount
              : unique.length,
          },
        };
      },
    });
  }, [fetchMore, variables.input, results.length, hasMore, isLoading, isFetchingMore]);

  return useMemo(
    () => ({
      isLoading,
      isFetchingMore,
      hasMore,
      loadMore,
      organizations: results,
      totalCount,
      searchText,
      setSearchText: () => {},
    }),
    [
      isLoading,
      isFetchingMore,
      hasMore,
      loadMore,
      results,
      totalCount,
      searchText,
    ]
  );
};
